/**
 * CTO Directive #101 — sensory organ presence client.
 *
 * Every device is an organ. ME never moves; only its senses change.
 * This module is the single place the browser tells ME "I'm here"
 * (attach), "still here" (heartbeat every 30s) and "leaving"
 * (detach on visibility hidden / unload).
 *
 * Silent — no UI for the user. ME observes; the user lives.
 */

import { api } from "@/lib/api";

const HEARTBEAT_INTERVAL_MS = 30_000;

/** Build a stable, anonymous signature so this exact browser/tab
 *  collapses to the same organ across reloads. */
function deriveSignature() {
  const parts = [
    navigator.userAgent || "ua",
    `${window.screen?.width || 0}x${window.screen?.height || 0}`,
    Intl.DateTimeFormat().resolvedOptions().timeZone || "tz",
    // localStorage tab token so a second tab is treated as a separate
    // organ. We never send this back to the server raw — just hash.
    (() => {
      const key = "me-organ-tab";
      let v = localStorage.getItem(key);
      if (!v) {
        v = Math.random().toString(36).slice(2, 10);
        localStorage.setItem(key, v);
      }
      return v;
    })(),
  ].join("|");
  // Cheap stable djb2 hash so the signature is short and opaque.
  let h = 5381;
  for (let i = 0; i < parts.length; i++) h = ((h << 5) + h) + parts.charCodeAt(i);
  return `web-${(h >>> 0).toString(36)}`;
}

/** Best-effort browser environment detection. ME never asks the user
 *  to configure these — they are observed and reported once. */
function detectKindAndCapabilities() {
  const ua = (navigator.userAgent || "").toLowerCase();
  let kind = "desktop";
  if (/iphone|android.*mobile|mobi/.test(ua)) kind = "phone";
  else if (/ipad|tablet/.test(ua)) kind = "tablet";
  else if (/tv|smart-tv|smarttv|webos|tizen/.test(ua)) kind = "television";

  const capabilities = new Set(["display"]);
  if (navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === "function") {
    capabilities.add("audio_in");
  }
  if (window.SpeechSynthesis || window.speechSynthesis) {
    capabilities.add("audio");
  }
  // Web Bluetooth is only present on Chromium-class browsers behind a
  // user gesture, but its presence is itself a permission-free signal.
  if (navigator.bluetooth) capabilities.add("sensor");
  // Web GPU / WebGL is a rough proxy for "this device can compose
  // heavy media". Both are exposed without permission.
  if (window.WebGLRenderingContext || window.WebGL2RenderingContext) {
    capabilities.add("media");
  }

  const metadata = {
    user_agent: navigator.userAgent || null,
    language: navigator.language || null,
    screen: {
      width: window.screen?.width || null,
      height: window.screen?.height || null,
      pixel_ratio: window.devicePixelRatio || null,
    },
    device_memory: navigator.deviceMemory || null,
    hardware_concurrency: navigator.hardwareConcurrency || null,
  };

  return { kind, capabilities: Array.from(capabilities), metadata };
}

/** A single organ instance. Construct once per page; start() returns
 *  a stop() function for cleanup. */
export function createOrganClient() {
  const signature = deriveSignature();
  const { kind, capabilities, metadata } = detectKindAndCapabilities();
  let organId = null;
  let heartbeatTimer = null;
  let isStarted = false;
  let stopped = false;

  async function attach() {
    try {
      const r = await api.post("/me/organs/attach", {
        kind, signature, label: `${kind} (web)`,
        capabilities, metadata,
      });
      organId = r.data?.id || null;
      return r.data;
    } catch {
      return null;
    }
  }

  async function heartbeat() {
    if (!organId) return;
    try { await api.post("/me/organs/heartbeat", { organ_id: organId }); }
    catch { /* silent */ }
  }

  async function detach() {
    if (!organId) return;
    try { await api.post("/me/organs/detach", { organ_id: organId }); }
    catch { /* silent */ }
    organId = null;
  }

  function _onVisibility() {
    if (document.visibilityState === "hidden") {
      if (heartbeatTimer) clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    } else if (document.visibilityState === "visible" && isStarted && !stopped) {
      // Browser came back — re-attach (idempotent on signature) and
      // restart the heartbeat. ME never misses a return.
      attach().then(() => {
        if (!heartbeatTimer && !stopped) {
          heartbeatTimer = setInterval(heartbeat, HEARTBEAT_INTERVAL_MS);
        }
      });
    }
  }

  function _onBeforeUnload() { detach(); }

  async function start() {
    if (isStarted) return;
    isStarted = true;
    await attach();
    heartbeatTimer = setInterval(heartbeat, HEARTBEAT_INTERVAL_MS);
    document.addEventListener("visibilitychange", _onVisibility);
    window.addEventListener("beforeunload", _onBeforeUnload);
  }

  function stop() {
    stopped = true;
    if (heartbeatTimer) clearInterval(heartbeatTimer);
    heartbeatTimer = null;
    document.removeEventListener("visibilitychange", _onVisibility);
    window.removeEventListener("beforeunload", _onBeforeUnload);
    detach();
  }

  return { start, stop };
}
